import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Phone, Globe, User, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { getTranslation } from '../translations/translations';
import LoginModal from './LoginModal';
import RegisterModal from './RegisterModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = ({ cartItemCount, onCartClick, onOrderClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const { language, changeLanguage } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = (key) => getTranslation(language, key);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'lv', name: 'Latviešu', flag: '🇱🇻' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    // If we're not on the home page, navigate to home first
    if (location.pathname !== '/home') {
      navigate('/home');
      // Wait for navigation, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    setIsMobileMenuOpen(false);
  };
  
  const goToHome = () => {
    navigate('/home');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-black/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div 
            className="flex items-center space-x-3 cursor-pointer group"
            onClick={goToHome}
          >
            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 relative overflow-hidden">
              <svg viewBox="0 0 48 48" className="w-10 h-10" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Japanese lantern/chochin style */}
                <ellipse cx="24" cy="14" rx="10" ry="4" fill="black"/>
                <rect x="14" y="14" width="20" height="20" rx="10" fill="black"/>
                <path d="M16 18 L32 18 M16 24 L32 24 M16 30 L32 30" stroke="white" strokeWidth="2"/>
                <ellipse cx="24" cy="34" rx="10" ry="4" fill="black"/>
                {/* Center vertical line */}
                <rect x="23" y="10" width="2" height="28" fill="black"/>
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-white font-bold text-xl tracking-tight">MIKADO</span>
              <span className="text-gray-400 text-xs tracking-widest">SUSHI</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={goToHome}
              className="text-white hover:text-gray-300 transition-colors duration-200 text-sm font-medium"
            >
              {t('home')}
            </button>
            <button
              onClick={() => scrollToSection('menu')}
              className="text-white hover:text-gray-300 transition-colors duration-200 text-sm font-medium"
            >
              {t('menu')}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-white hover:text-gray-300 transition-colors duration-200 text-sm font-medium"
            >
              {t('about')}
            </button>
            <button
              onClick={() => scrollToSection('delivery')}
              className="text-white hover:text-gray-300 transition-colors duration-200 text-sm font-medium"
            >
              {t('delivery')}
            </button>
            <a
              href="tel:+37128717331"
              className="flex items-center space-x-2 text-white hover:text-gray-300 transition-colors duration-200"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">+371 28717331</span>
            </a>
          </nav>

          {/* Right Actions */}
          <div className="flex items-center space-x-4">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:text-gray-300 hover:bg-white/10 h-10 px-3"
                >
                  <Globe className="w-5 h-5 mr-2" />
                  <span className="text-sm font-medium uppercase">{language}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border-2 border-gray-200">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`cursor-pointer ${language === lang.code ? 'bg-gray-100 font-bold' : ''}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Profile / Login */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-white hover:text-gray-300 hover:bg-white/10 h-10 px-3"
                  >
                    <User className="w-5 h-5 mr-2" />
                    <span className="text-sm font-medium">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border-2 border-gray-200">
                  <DropdownMenuItem
                    onClick={() => navigate('/profile')}
                    className="cursor-pointer"
                  >
                    <User className="w-4 h-4 mr-2" />
                    {t('myAccount')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={logout}
                    className="cursor-pointer text-red-600"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                onClick={() => setShowLoginModal(true)}
                variant="ghost"
                size="sm"
                className="text-white hover:text-gray-300 hover:bg-white/10 h-10 px-3"
              >
                <User className="w-5 h-5 mr-2" />
                <span className="text-sm font-medium">{t('login')}</span>
              </Button>
            )}
            
            <button
              onClick={onCartClick}
              className="relative p-2 text-white hover:text-gray-300 transition-colors duration-200"
            >
              <ShoppingCart className="w-6 h-6" />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-white text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </button>
            <Button
              onClick={onOrderClick}
              className="hidden md:inline-flex bg-white text-black hover:bg-gray-200 font-medium px-6 transition-all duration-300"
            >
              {t('orderNow')}
            </Button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-white p-2"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-black/98 backdrop-blur-md border-t border-white/10">
          <nav className="flex flex-col space-y-4 px-4 py-6">
            <button
              onClick={goToHome}
              className="text-white hover:text-gray-300 transition-colors duration-200 text-left font-medium"
            >
              {t('home')}
            </button>
            <button
              onClick={() => scrollToSection('menu')}
              className="text-white hover:text-gray-300 transition-colors duration-200 text-left font-medium"
            >
              {t('menu')}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-white hover:text-gray-300 transition-colors duration-200 text-left font-medium"
            >
              {t('about')}
            </button>
            <button
              onClick={() => scrollToSection('delivery')}
              className="text-white hover:text-gray-300 transition-colors duration-200 text-left font-medium"
            >
              {t('delivery')}
            </button>
            <a
              href="tel:+37128717331"
              className="flex items-center space-x-2 text-white hover:text-gray-300 transition-colors duration-200"
            >
              <Phone className="w-4 h-4" />
              <span className="font-medium">+371 28717331</span>
            </a>
            <Button
              onClick={onOrderClick}
              className="bg-white text-black hover:bg-gray-200 font-medium w-full"
            >
              {t('orderNow')}
            </Button>
          </nav>
        </div>
      )}

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSwitchToRegister={() => {
          setShowLoginModal(false);
          setShowRegisterModal(true);
        }}
      />

      <RegisterModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSwitchToLogin={() => {
          setShowRegisterModal(false);
          setShowLoginModal(true);
        }}
      />
    </header>
  );
};

export default Header;